import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, CheckCircle, X, AlertTriangle, Clock, Mail, Video, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';
import { LEADERSHIP_INTERVIEW_LINKS } from '../constants/industries';

// iter166 — Reusable popup-style shell that matches the NDA + MFA
// popup layouts: hero on top, content card in the middle, anchored
// olive CTA at the bottom. Light-mode only.
const EmbeddedShell = ({ icon: Icon, title, children, footer }) => (
  <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
    <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
      <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
        <Icon className="w-8 h-8 text-[#757150]" />
      </div>
      <h2 className="text-2xl font-bold text-[#1c1a1c]">{title}</h2>
    </div>
    <div className="px-6 flex-1 flex flex-col justify-center">{children}</div>
    {footer && (
      <div
        className="px-6 pt-4 pb-6 bg-white"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        {footer}
      </div>
    )}
  </div>
);

const LeadershipInterviewPage = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const embedded = useEmbeddedMode();
  const [showBooking, setShowBooking] = useState(false);
  const [viewStep, setViewStep] = useState('initial'); // initial, booking, confirm, booked
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [requestingUpdate, setRequestingUpdate] = useState(false);
  const [lastUpdateRequest, setLastUpdateRequest] = useState(null);

  // iter175ca — Booking URL now comes from the admin-controlled
  // per-role overrides (system_settings.onboarding_role_overrides.
  // leadership_interview_links.<role>). The legacy hardcoded
  // constants in industries.js are kept as the fallback inside the
  // backend endpoint itself, so the user never sees a missing link
  // even if no override is configured.
  const [bookingLink, setBookingLink] = useState(
    LEADERSHIP_INTERVIEW_LINKS[user?.role] || LEADERSHIP_INTERVIEW_LINKS['affiliate']
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/me/onboarding/leadership-interview-link');
        if (!cancelled && r?.data?.url) setBookingLink(r.data.url);
      } catch (_e) { /* keep hardcoded fallback */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Check user status
  const isPending = user?.step2d_interview_pending;
  const isRejected = user?.step2d_interview_rejected;
  const isApproved = user?.step2d_leadership_interview;

  // Check if user can request update (only once per 7 days)
  useEffect(() => {
    const stored = localStorage.getItem(`last_update_request_${user?.id}`);
    if (stored) {
      setLastUpdateRequest(new Date(stored));
    }
  }, [user?.id]);

  const canRequestUpdate = () => {
    if (!lastUpdateRequest) return true;
    const now = new Date();
    const diff = (now - lastUpdateRequest) / (1000 * 60 * 60 * 24); // days
    return diff >= 7;
  };

  const getDaysUntilNextRequest = () => {
    if (!lastUpdateRequest) return 0;
    const now = new Date();
    const diff = 7 - Math.floor((now - lastUpdateRequest) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  // Calculate days until reset if rejected
  const getDaysUntilReset = () => {
    if (user?.step2d_rejection_date) {
      const rejectionDate = new Date(user.step2d_rejection_date);
      const resetDate = new Date(rejectionDate);
      resetDate.setDate(resetDate.getDate() + 60);
      const now = new Date();
      const diff = Math.ceil((resetDate - now) / (1000 * 60 * 60 * 24));
      return Math.max(0, diff);
    }
    return 60;
  };

  const handleConfirmBooking = async () => {
    setRequestingApproval(true);
    try {
      await api.post('/users/request-interview-approval');
      await refreshUser();
      toast.success('Interview confirmed! We\'ll review your application.');
      setViewStep('booked');
      if (embedded) postEmbeddedStepComplete('step2e_leadership_interview');
    } catch (error) {
      toast.error('Failed to confirm. Please try again.');
    } finally {
      setRequestingApproval(false);
    }
  };

  const handleRequestUpdate = async () => {
    if (!canRequestUpdate()) {
      toast.error(`You can request an update in ${getDaysUntilNextRequest()} days`);
      return;
    }

    setRequestingUpdate(true);
    try {
      await api.post('/request-update', { request_type: 'leadership_interview_progression' });
      const now = new Date();
      localStorage.setItem(`last_update_request_${user?.id}`, now.toISOString());
      setLastUpdateRequest(now);
      toast.success('Update requested! An admin will review your status.');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to request update';
      toast.error(errorMessage);
    } finally {
      setRequestingUpdate(false);
    }
  };

  const handleExitBooking = () => {
    setShowBooking(false);
    setViewStep('confirm');
  };

  // Olive primary CTA used inside the embedded shell.
  const olivePrimary = "w-full h-12 rounded-xl flex items-center justify-center gap-2 text-base shadow-sm bg-[#757150] hover:bg-[#5c5840] text-white disabled:opacity-60";
  const oliveOutline = "w-full h-11 rounded-xl flex items-center justify-center gap-2 text-sm bg-white border border-[#ece9df] text-[#1c1a1c] hover:border-[#c9c4a8]";

  // ============================================================
  // EMBEDDED VIEWS (popup) — match NDA + MFA popup styling
  // ============================================================
  if (embedded) {
    // Booking iframe — render bare iframe with NO chrome (mirrors the
    // embedded NDA-form pattern).
    if (showBooking) {
      return (
        <div className="fixed inset-0 bg-white flex flex-col">
          <iframe
            src={bookingLink}
            className="w-full h-full flex-1 border-0"
            title="Book Leadership Interview"
            allow="camera; microphone"
          />
        </div>
      );
    }

    // Approved
    if (isApproved) {
      return (
        <EmbeddedShell
          icon={CheckCircle}
          title="Interview Completed"
          footer={null}
        >
          <p className="text-[15px] text-[#3a3a3a] leading-[1.6]">
            Congratulations — you've completed your leadership interview and have been
            approved to continue onboarding. Close this popup to head back to the home page.
          </p>
        </EmbeddedShell>
      );
    }

    // Rejected / on hold
    if (isRejected) {
      const daysLeft = getDaysUntilReset();
      return (
        <EmbeddedShell icon={AlertTriangle} title="Application On Hold" footer={null}>
          <p className="text-[15px] text-[#3a3a3a] leading-[1.6] mb-4">
            We were not able to proceed with your candidacy at this moment.
          </p>
          <div className="bg-[#f5f5f5] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#757150]" />
              <span className="text-sm font-medium text-[#1c1a1c]">Onboarding Reset</span>
            </div>
            <p className="text-sm text-[#5a5a5a] leading-snug">
              Unsuccessful onboarding will reset in <strong className="text-[#1c1a1c]">{daysLeft} days</strong>.
              We encourage you to reapply at that time.
            </p>
          </div>
        </EmbeddedShell>
      );
    }

    // Pending — booked, awaiting approval
    if (isPending || viewStep === 'booked') {
      return (
        <EmbeddedShell
          icon={Video}
          title="Interview Booked"
          footer={
            <div className="space-y-2">
              <Button
                className={oliveOutline}
                onClick={handleRequestUpdate}
                disabled={requestingUpdate || !canRequestUpdate()}
                data-testid="request-update-btn"
              >
                <RefreshCw className={`w-4 h-4 ${requestingUpdate ? 'animate-spin' : ''}`} />
                {canRequestUpdate()
                  ? 'Request an Update'
                  : `Update available in ${getDaysUntilNextRequest()}d`}
              </Button>
            </div>
          }
        >
          <p className="text-[15px] text-[#3a3a3a] leading-[1.6] mb-4">
            Thanks for booking your interview. Please check your email for the booking
            confirmation and calendar invite.
          </p>
          <div className="bg-[#f5f5f5] rounded-lg p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-[#757150]" />
              <span className="text-sm font-medium text-[#1c1a1c]">Interview via Microsoft Teams</span>
            </div>
            <p className="text-sm text-[#5a5a5a] leading-snug">
              The interview will be conducted via Microsoft Teams. We look forward to connecting soon.
            </p>
          </div>
          <div className="bg-[#f5f5f5] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#757150]" />
              <span className="text-sm font-medium text-[#1c1a1c]">What's Next?</span>
            </div>
            <p className="text-sm text-[#5a5a5a] leading-snug">
              After your interview, please check back here for a status update. You can expect
              an update within <strong className="text-[#1c1a1c]">2 weeks</strong>. If it has
              been longer, request an update below.
            </p>
          </div>
        </EmbeddedShell>
      );
    }

    // Confirmation step — verify email
    if (viewStep === 'confirm') {
      return (
        <EmbeddedShell
          icon={Mail}
          title="Verify Your Booking"
          footer={
            <div className="space-y-2">
              <Button
                className={olivePrimary}
                onClick={handleConfirmBooking}
                disabled={requestingApproval}
                data-testid="confirm-booking-btn"
              >
                <CheckCircle className="w-4 h-4" />
                {requestingApproval ? 'Confirming…' : 'Yes, I have the email confirmation'}
              </Button>
              <Button
                className={oliveOutline}
                onClick={() => setShowBooking(true)}
                data-testid="rebook-btn"
              >
                <Calendar className="w-4 h-4" />
                No, let me book again
              </Button>
            </div>
          }
        >
          <p className="text-[15px] text-[#3a3a3a] leading-[1.6] mb-4">
            Please verify that you successfully booked the appointment. You should have
            received an <strong className="text-[#1c1a1c]">email confirmation</strong> with your
            appointment details.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Important</p>
                <p className="text-sm text-yellow-700 leading-snug">
                  Only confirm if you have received the email confirmation. Once confirmed,
                  you will not be able to re-book.
                </p>
              </div>
            </div>
          </div>
        </EmbeddedShell>
      );
    }

    // Initial — invite user to book
    return (
      <EmbeddedShell
        icon={Calendar}
        title="Schedule Your Interview"
        footer={
          <div className="space-y-2">
            <Button
              className={olivePrimary}
              onClick={() => setShowBooking(true)}
              data-testid="book-now-btn"
            >
              <Calendar className="w-4 h-4" />
              Book my interview
            </Button>
            <Button
              className={oliveOutline}
              onClick={() => setViewStep('confirm')}
              data-testid="already-booked-btn"
            >
              <CheckCircle className="w-4 h-4" />
              I've already booked
            </Button>
          </div>
        }
      >
        <p className="text-[15px] text-[#3a3a3a] leading-[1.6] mb-4">
          As part of the onboarding process, you need to complete a leadership interview.
          This is a brief call with our leadership team to discuss your role and expectations.
        </p>
        <div className="bg-[#f5f5f5] rounded-lg p-4">
          <p className="text-sm text-[#5a5a5a] leading-snug">
            <strong className="text-[#1c1a1c]">Note:</strong> After booking your interview,
            you'll need to wait for admin approval before proceeding to the next step.
          </p>
        </div>
      </EmbeddedShell>
    );
  }

  // ============================================================
  // STANDALONE /leadership-interview ROUTE — original layout
  // (dark-mode classes preserved for the standalone page).
  // ============================================================

  if (isApproved) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4">
        <div className="max-w-md mx-auto pt-6">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Leadership Interview</h1>
          </div>
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Interview Completed</h2>
              <p className="text-muted-foreground mb-6">
                Congratulations! You've completed your leadership interview and have been approved to continue.
              </p>
              <Button onClick={() => navigate('/')} className="w-full">
                Continue Onboarding
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isRejected) {
    const daysLeft = getDaysUntilReset();
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4">
        <div className="max-w-md mx-auto pt-6">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Leadership Interview</h1>
          </div>
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-12 h-12 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Application On Hold</h2>
              <p className="text-muted-foreground mb-4">
                We were not able to proceed with your candidacy at this moment.
              </p>
              <div className="bg-muted/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Onboarding Reset</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Unsuccessful onboarding will reset in <strong>{daysLeft} days</strong>. We encourage you to reapply at that time.
                </p>
              </div>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isPending || viewStep === 'booked') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4">
        <div className="max-w-md mx-auto pt-6">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Leadership Interview</h1>
          </div>
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <Video className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-xl font-bold">You've Booked Your Interview</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Thanks for booking your interview. Please check your email for the booking confirmation and calendar invite.
              </p>
              <div className="bg-muted/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Interview via Microsoft Teams</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  The interview will be conducted via Microsoft Teams. We look forward to connecting soon.
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">What's Next?</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  After your interview, please check back here for a status update. You can expect an update within <strong>2 weeks</strong> of your interview appointment. If it has been longer than that, please "Request an Update" below.
                </p>
              </div>
              <div className="space-y-3">
                <Button onClick={() => navigate('/')} className="w-full">
                  Return to Onboarding
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRequestUpdate}
                  disabled={requestingUpdate || !canRequestUpdate()}
                  className="w-full"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${requestingUpdate ? 'animate-spin' : ''}`} />
                  {canRequestUpdate()
                    ? 'Request an Update'
                    : `Request available in ${getDaysUntilNextRequest()} days`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showBooking) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-[#0d0d0d] z-[100] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#dfdfdf] dark:border-[#333] bg-white dark:bg-[#141414]">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-[#757150]" />
            <span className="font-semibold">Book Leadership Interview</span>
          </div>
          <Button variant="outline" onClick={handleExitBooking} className="gap-2">
            <X className="w-4 h-4" />
            Exit & Continue
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            src={bookingLink}
            className="w-full h-full border-0"
            title="Book Leadership Interview"
          />
        </div>
      </div>
    );
  }

  if (viewStep === 'confirm') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4">
        <div className="max-w-md mx-auto pt-6">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Leadership Interview</h1>
          </div>
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#757150]" />
                </div>
                <h3 className="font-semibold">Verify Your Booking</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Please verify that you successfully booked the appointment. You should have received an <strong>email confirmation</strong> with your appointment details.
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Important</p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Only confirm if you have received the email confirmation. Once confirmed, you will not be able to re-book.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={handleConfirmBooking}
                  disabled={requestingApproval}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {requestingApproval ? 'Confirming...' : 'Yes, I Have the Email Confirmation'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowBooking(true)}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  No, Let Me Book Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24">
      <div className="max-w-md mx-auto pt-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Leadership Interview</h1>
            <p className="text-sm text-muted-foreground">Step 2E: Schedule your interview</p>
          </div>
        </div>

        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-4">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#757150]" />
              </div>
              <h2 className="text-xl font-bold">Schedule Your Interview</h2>
            </div>
            <p className="text-muted-foreground mb-4">
              As part of the onboarding process, you need to complete a leadership interview. This is a brief call with our leadership team to discuss your role and expectations.
            </p>
            <div className="bg-muted/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> After booking your interview, you'll need to wait for admin approval before proceeding to the next step.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Have you booked your leadership interview?</h3>
            <div className="space-y-3">
              <Button className="w-full" onClick={() => setShowBooking(true)}>
                <Calendar className="w-4 h-4 mr-2" />
                No, let me book now
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setViewStep('confirm')}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Yes, I've booked my interview
              </Button>
            </div>
          </CardContent>
        </Card>

        <button
          onClick={() => navigate('/')}
          className="w-full text-center mt-4 text-sm text-muted-foreground hover:text-foreground"
        >
          I'll do this later
        </button>
      </div>
    </div>
  );
};

export default LeadershipInterviewPage;
